import { useEffect, useState } from 'react'

import { formatRelative } from 'date-fns'
import { MoveRight, ThumbsDown, ThumbsUp } from 'lucide-react'
import { toast } from 'sonner'

import { capitaliseWords, cn } from '@fortem-monorepo/js-utilities'
import { Separator, Tooltip, TooltipContent, TooltipTrigger } from '@fortem-monorepo/react-ui'
import { PnlAttributions } from '@fortem-monorepo/web-sdk'

import { AttributionsActionedBy } from '@/pages/operations/pnl-attributions/components/AttributionsActionedBy'
import { InspectRequestDialog } from '@/pages/operations/pnl-attributions/dialogs/InspectRequestDialog'
import { formatNumber } from '@/utilities/format'

export interface HistoryItemProps {
  row: PnlAttributions.AttributionRequestRow
}

export const HistoryItem: React.FC<HistoryItemProps> = ({ row }) => {
  const isPending = row.status === 'PENDING'
  const isApplied = row.status === 'APPLIED'
  const isRejected = row.status === 'REJECTED'

  const isComponent = row.request_type === 'COMPONENT'
  const isAdjustment = row.request_type === 'PNL_ADJUSTMENT'

  const isTrade = row.target_level === 'TRADE'
  const isPosition = row.target_level === 'POSITION'

  const prevPnL = row.base_net_pnl ?? 0
  const newPnL = row.target_net_pnl ?? 123.99
  const changePnL = row.total_amount_base

  const [inspecting, setInspecting] = useState(false)

  useEffect(() => {
    return () => {
      setInspecting(false)
    }
  }, [])

  return (
    <>
      <InspectRequestDialog open={inspecting} onOpenChange={setInspecting} request={row} />
      <div
        className={cn('p-8 rounded-md hover:bg-slate-50 overflow-hidden border cursor-pointer', {
          'border-emerald-400': isApplied,
          'border-amber-400': isPending,
        })}
        onClick={() => {
          setInspecting(true)
        }}
      >
        <div className="flex items-center gap-1">
          <AttributionsActionedBy name={row.requested_by_name} />
          <span className="text-sm text-muted-foreground/80">requested an update</span>
          <TimelineDate date={row.requested_datetime_utc} />
        </div>
        <div className={'mt-3'}>
          <div className={'text-sm text-heading'}>
            {isAdjustment && 'Adjust net PnL'}
            {isComponent && (
              <>
                Add new{' '}
                <span
                  className={
                    'px-2 py-0.5 uppercase text-[11px] font-medium bg-blue-100 text-heading tracking-wide rounded-md'
                  }
                >
                  {capitaliseWords(row.component_type)}
                </span>{' '}
                component
              </>
            )}{' '}
            {isAdjustment && isTrade && (
              <>
                of trade <TradeBadge trade_id={row.trade_id} /> for{' '}
                <span className={'font-medium'}>{row.unique_identifier}</span>
              </>
            )}
            {isAdjustment && isPosition && (
              <>
                for position with <span className={'font-medium'}>{row.unique_identifier}</span>
              </>
            )}
            {isComponent && isTrade && (
              <>
                to trade <TradeBadge trade_id={row.trade_id} /> for{' '}
                <span className={'font-medium'}>{row.unique_identifier}</span>
              </>
            )}
            {isComponent && isPosition && (
              <>
                for position with <span className={'font-medium'}>{row.unique_identifier}</span>
              </>
            )}
            {isAdjustment && (
              <div className={'flex items-center gap-2 mt-4'}>
                <span className={cn(numberRules(prevPnL))}>{formatNumber(prevPnL)}</span>
                <MoveRight size={16} className={'text-muted-foreground/50'} />
                <span className={cn(numberRules(newPnL))}>{formatNumber(newPnL)}</span>
                <span className={cn('ml-4 opacity-50', numberRules(changePnL))}>
                  ({changePnL > 0 && '+'}
                  {formatNumber(changePnL)})
                </span>
              </div>
            )}
            {isComponent && (
              <div className={'flex items-center gap-2 mt-4'}>
                <span className={cn(numberRules(row.total_amount_base))}>{formatNumber(row.total_amount_base)}</span>
              </div>
            )}
            <div className={'mt-2 italic text-muted-foreground p-2 rounded-md'}>"{row.reason}"</div>
          </div>
        </div>
        {!isPending && (
          <>
            <Separator className={'mt-4'} />
            <div className={'mt-4'}>
              <div className={'flex items-center gap-1 text-sm text-heading'}>
                {row.approved_by_name && <AttributionsActionedBy name={row.approved_by_name} />}
                <span
                  className={cn('inline-flex items-center gap-1', {
                    'text-emerald-600': isApplied,
                    'text-red-600': isRejected,
                  })}
                >
                  {isApplied && (
                    <>
                      <ThumbsUp size={16} />
                      approved
                    </>
                  )}
                  {isRejected && (
                    <>
                      <ThumbsDown size={16} />
                      rejected
                    </>
                  )}
                </span>
                <span className={'text-muted-foreground/80'}>the request</span>
                <TimelineDate date={row.approved_datetime_utc} />
              </div>
              {row.decision_comment && (
                <div className={'mt-2 italic text-sm text-muted-foreground p-2 rounded-md'}>
                  "{row.decision_comment}"
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  )
}

const numberRules = (number: number) => {
  return cn('font-mono text-muted-foreground text-sm', {
    'text-emerald-600': number > 0,
    'text-red-700': number < 0,
  })
}

interface TradeBadgeProps extends React.ComponentProps<'div'> {
  trade_id?: string | null
}

const TradeBadge: React.FC<TradeBadgeProps> = ({ trade_id, children, className, ...props }) => {
  const [copiedTradeId, setCopiedTradeId] = useState<string | null>(null)

  const copyTradeId = async (tradeId: string) => {
    await navigator.clipboard.writeText(tradeId)

    setCopiedTradeId(tradeId)

    toast.success('Copied to clipboard', {
      description: tradeId,
      duration: 1000,
    })
  }

  useEffect(() => {
    if (copiedTradeId === null) return

    const timeoutId = window.setTimeout(() => {
      setCopiedTradeId(null)
    }, 1000)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [copiedTradeId])

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex items-center justify-center max-w-30 px-2 py-px border border-yellow-200 text-xs bg-yellow-100 rounded-md font-mono',
            cn({
              'cursor-pointer': !copiedTradeId,
            }),
            className,
          )}
          onClick={(e) => {
            e.stopPropagation()
            trade_id && copyTradeId(trade_id)
          }}
          {...props}
        >
          <span className={'inline-block truncate'}>{trade_id}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        Copy Trade ID
        <br />
        {trade_id}
      </TooltipContent>
    </Tooltip>
  )
}

export interface TimelineDateProps {
  date: string | null | undefined
}

export const TimelineDate: React.FC<TimelineDateProps> = ({ date }) => {
  return (
    <div className={'inline-flex items-center'}>
      <div className={'size-1 bg-gray-200 rounded-full mx-2'} />
      {date && <span className={'text-sm text-muted-foreground/50'}>{formatRelative(new Date(date), new Date())}</span>}
    </div>
  )
}
